import { useCallback, useEffect, useRef } from "react";

// usePolledJob — encapsulates the CLAUDE.md background-job polling pattern:
// a 202/Processing kickoff followed by a setInterval that polls a job-status
// proc until the job reaches a terminal status.
//
//   const poll = usePolledJob();
//   poll.start(async (stop) => { ...check status; on terminal: stop(); ... }, 5000);
//
// Behavior (matches the loops this replaced):
// - the live interval is held in a ref; start() replaces any interval already
//   running (equivalent to the old clearInterval-before-set guard);
// - the supplied poll function receives stop() and calls it itself at its
//   Complete/Failed points — clearing happens exactly where the old inline
//   clearInterval calls sat, so follow-up work (data refresh, alerts) keeps
//   its original ordering;
// - a tick that throws is tolerated: one failed poll (cold start, blip) must
//   not kill the interval. Callers keep their own try/catch inside the poll
//   function when they want to log the error;
// - a mount-scoped effect clears any live interval on unmount, so navigating
//   away mid-job doesn't leave an interval hitting sqlRead forever.
export function usePolledJob() {
  const intervalRef = useRef(null);

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const start = useCallback((pollFn, intervalMs) => {
    stop();
    intervalRef.current = setInterval(async () => {
      try {
        await pollFn(stop);
      } catch {
        // transient poll error — keep polling
      }
    }, intervalMs);
  }, [stop]);

  useEffect(() => stop, [stop]);

  return { start, stop };
}
